import { useEffect, useRef, useState } from "react";
import { UploadCloud, File as FileIcon, Trash2, Download, Loader2, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MAX_BYTES = 50 * 1024 * 1024;

const formatSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

export default function PortalAssetVault() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState([]); // {id, name, progress, status: 'uploading'|'done'|'error'}
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/assets");
      setAssets(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadOne = async (file) => {
    if (file.size > MAX_BYTES) {
      toast.error(`ERROR · "${file.name}" exceeds 50 MB limit`);
      return;
    }
    const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploads((u) => [
      ...u,
      { id: tempId, name: file.name, progress: 0, status: "uploading" },
    ]);

    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/portal/assets", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 50;
          setUploads((u) =>
            u.map((x) => (x.id === tempId ? { ...x, progress: pct } : x))
          );
        },
      });
      setUploads((u) =>
        u.map((x) =>
          x.id === tempId ? { ...x, progress: 100, status: "done" } : x
        )
      );
      toast.success(`OK · "${file.name}" uploaded`);
      // Refresh list
      load();
      // Auto-dismiss done pill after 3s
      setTimeout(() => {
        setUploads((u) => u.filter((x) => x.id !== tempId));
      }, 3000);
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed";
      toast.error(`ERROR · ${msg}`);
      setUploads((u) =>
        u.map((x) => (x.id === tempId ? { ...x, status: "error" } : x))
      );
      setTimeout(() => {
        setUploads((u) => u.filter((x) => x.id !== tempId));
      }, 4000);
    }
  };

  const handleFiles = (files) => {
    for (const f of Array.from(files)) uploadOne(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const remove = async (asset) => {
    setPendingDelete(asset);
  };

  const confirmDelete = async () => {
    const asset = pendingDelete;
    if (!asset) return;
    setPendingDelete(null);
    try {
      await api.delete(`/portal/assets/${asset.id}`);
      setAssets((a) => a.filter((x) => x.id !== asset.id));
      toast.success("OK · asset removed");
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed";
      toast.error(`ERROR · ${msg}`);
    }
  };

  const download = async (asset) => {
    try {
      const res = await api.get(`/portal/assets/${asset.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = asset.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("ERROR · download failed");
    }
  };

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 border-t border-[#1F2833]"
      data-testid="portal-asset-vault"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-2">
            // ASSET.VAULT
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
            Drop brand assets here.
          </h2>
        </div>
        <div className="font-mono text-[11px] text-[#C5C6C7]/70">
          {assets.length} file{assets.length !== 1 ? "s" : ""} · 50 MB max each
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="asset-dropzone"
        className={`cursor-pointer border-2 border-dashed transition-all p-10 lg:p-14 text-center ${
          dragActive
            ? "border-emerald-400 bg-emerald-400/5"
            : "border-[#1F2833] hover:border-[#FF3355]/60 hover:bg-[#0d1117]"
        }`}
      >
        <UploadCloud
          size={32}
          className={`mx-auto ${dragActive ? "text-emerald-400" : "text-[#FF3355]"}`}
        />
        <div className="mt-4 font-display text-lg font-semibold text-[#F5F5F7]">
          {dragActive ? "Release to upload" : "Drag & drop or click to browse"}
        </div>
        <div className="mt-2 font-mono text-[11px] text-[#C5C6C7]/70">
          Images · PDFs · Docs · anything up to 50 MB
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          data-testid="asset-file-input"
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
      </div>

      {/* Upload progress pills */}
      {uploads.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="asset-upload-progress">
          {uploads.map((u) => (
            <div
              key={u.id}
              className="bg-[#0d1117] border border-[#1F2833] px-4 py-3 flex items-center gap-3"
            >
              {u.status === "done" ? (
                <Check size={14} className="text-emerald-400" />
              ) : u.status === "error" ? (
                <X size={14} className="text-[#FF3355]" />
              ) : (
                <Loader2 size={14} className="animate-spin text-[#FF3355]" />
              )}
              <span className="font-mono text-xs text-[#F5F5F7] truncate flex-1">
                {u.name}
              </span>
              <div className="w-32 h-1.5 bg-[#1F2833] relative overflow-hidden hidden sm:block">
                <div
                  className={`absolute inset-y-0 left-0 transition-all ${
                    u.status === "error"
                      ? "bg-[#FF3355]"
                      : u.status === "done"
                      ? "bg-emerald-400"
                      : "bg-[#FF3355]"
                  }`}
                  style={{ width: `${u.progress}%` }}
                />
              </div>
              <span className="font-mono text-[10px] text-[#C5C6C7]/70 w-10 text-right">
                {u.status === "error" ? "err" : `${u.progress}%`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Asset list */}
      <div className="mt-6">
        {loading ? (
          <div className="font-mono text-sm text-[#C5C6C7]">
            &gt; loading vault<span className="te-caret">_</span>
          </div>
        ) : assets.length === 0 ? (
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C5C6C7]/50 py-6 text-center">
            &gt; vault empty · drop your first asset above
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {assets.map((a) => (
              <div
                key={a.id}
                data-testid={`asset-item-${a.id}`}
                className="bg-[#0d1117] border border-[#1F2833] p-4 flex items-start gap-3 hover:border-[#FF3355]/50 transition-colors group"
              >
                <div className="h-10 w-10 flex items-center justify-center border border-[#1F2833] bg-[#0B0C10] text-[#FF3355] shrink-0">
                  <FileIcon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-[#F5F5F7] truncate">
                    {a.filename}
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-[#C5C6C7]/60">
                    {formatSize(a.size)} · {formatDate(a.created_at)}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => download(a)}
                    data-testid={`asset-download-${a.id}`}
                    className="p-1.5 text-[#C5C6C7] hover:text-emerald-400 transition-colors"
                    title="Download"
                  >
                    <Download size={13} />
                  </button>
                  <button
                    onClick={() => remove(a)}
                    data-testid={`asset-delete-${a.id}`}
                    className="p-1.5 text-[#C5C6C7] hover:text-[#FF3355] transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Themed delete confirmation dialog */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent
          data-testid="asset-delete-confirm"
          className="bg-[#0d1117] border border-[#1F2833] text-[#F5F5F7]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Delete asset?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#C5C6C7] font-mono text-xs">
              &gt; This will permanently remove{" "}
              <span className="text-[#FF3355]">&quot;{pendingDelete?.filename}&quot;</span> from your vault.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="asset-delete-cancel"
              className="bg-transparent border border-[#1F2833] text-[#C5C6C7] hover:bg-[#1F2833] hover:text-[#F5F5F7] font-mono text-[11px] uppercase tracking-[0.22em]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="asset-delete-confirm-btn"
              onClick={confirmDelete}
              className="bg-[#FF3355] text-[#0B0C10] hover:bg-[#B91C2C] font-mono text-[11px] uppercase tracking-[0.22em] font-bold"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
